import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Search from '@ingka/search';
import ListView, { ListViewItem } from '@ingka/list-view';

import { ModalComponentBaseProps } from '@pcmp/react-providers';
import { ModalHeader, Sheets, ModalBody, ModalFooter } from '@ingka/modal';
import { useDebounceValue } from 'usehooks-ts';
import {
  useFindCoWorkers,
  useGetUserProfileImageQuery,
} from '@/redux/graph/hooks';
import { LoadingState } from '@/components/helpers/loading';
import { GenericErrorCard } from '@/components/data/error';
import Button from '@ingka/button';
import { useContentTracking } from '@/lib/hooks/tracking';
import { useUser } from '@/redux/features/session/hooks';
import { useActiveLocation } from '@/redux/features/shopping-bag/hooks';
import { isTruthy } from '@pcmp-checka/lib-utilities/array';

const DEFAULT_PROFILE_IMAGE = '/default-profile-gray.svg';

const UserPickerItem: FC<{
  id?: string;
  name?: string;
  entry: { id: string; mail: string; displayName?: string };
  selected: boolean;
  onSelect: () => void;
}> = ({ id, name, entry, selected, onSelect }) => {
  const { data: imageUrl } = useGetUserProfileImageQuery({ email: entry.mail });

  return (
    <ListViewItem
      id={id}
      name={name}
      emphasised={true}
      control="radiobutton"
      title={entry.displayName}
      description={entry.mail}
      image={imageUrl || DEFAULT_PROFILE_IMAGE}
      imageAltText={entry.displayName}
      imageProps={{ className: 'rounded-full' }}
      onChange={onSelect}
      controlProps={{ value: entry.mail, checked: selected }}
      aria-label={entry.displayName}
    />
  );
};

export type FindUserData = {
  displayName: string;
  email: string;
  manager: string;
};

const UserPicker: FC<{
  search: string;
  selectedUser?: FindUserData;
  onUserSelect: (user: FindUserData) => void;
  filterByLocation?: 'include' | 'exclude';
  displayCurrentUser?: boolean;
}> = ({
  search,
  selectedUser,
  onUserSelect,
  displayCurrentUser,
  filterByLocation,
}) => {
  // Hooks
  const user = useUser();
  const { data: location } = useActiveLocation();
  const locationNames = location?.cdsLocation
    ?.map((entry) => entry?.name)
    .filter(isTruthy);
  const filter = locationNames?.length
    ? filterByLocation === 'include'
      ? {
          locations: locationNames,
        }
      : filterByLocation === 'exclude'
      ? { excludeLocations: locationNames }
      : undefined
    : undefined;
  const {
    error,
    data: users,
    isLoading,
  } = useFindCoWorkers(search || user.email, filter);
  // Data
  const filteredUsers = useMemo(() => {
    let data = users;
    const includeCurrentUser =
      displayCurrentUser && !data.find(({ mail }) => mail === user.email);

    if (includeCurrentUser)
      data = [
        {
          id: 'display-user',
          mail: user.email,
          displayName: user.displayName,
          manager: {
            mail: user.manager.mail || '',
            displayName: user.manager.displayName || '',
          },
        },
        ...data,
      ];

    const includeSelectedUser = !data.find(
      ({ mail }) => mail === selectedUser?.email
    );

    if (includeSelectedUser && selectedUser)
      data = [
        {
          id: 'selected-user',
          mail: selectedUser.email,
          displayName: selectedUser.displayName,
          manager: {
            mail: selectedUser.manager,
            displayName: selectedUser.manager,
          },
        },
        ...data,
      ];

    return data;
  }, [users, displayCurrentUser, user, selectedUser]);

  if (isLoading) return <LoadingState className="pt-5" />;

  if (error) return <GenericErrorCard className="pt-6" error={error} card />;

  return (
    <ListView id="userList">
      {filteredUsers.map((entry) => (
        <UserPickerItem
          key={entry.id}
          entry={entry}
          selected={!!selectedUser && selectedUser.email === entry.mail}
          onSelect={() =>
            onUserSelect({
              email: entry.mail,
              displayName: entry.displayName || entry.mail,
              manager: entry.manager?.mail || '',
            })
          }
        />
      ))}
    </ListView>
  );
};

const FindUserSider: FC<
  ModalComponentBaseProps & {
    onBack?: () => void;
    descriptionText: string;
    onConfirm: (selectedUser: FindUserData) => void;
    filterByLocation?: 'include' | 'exclude';
    user?: FindUserData;
    displayCurrentUser?: boolean;
  }
> = ({
  handleDismiss,
  descriptionText,
  onConfirm,
  onBack,
  filterByLocation,
  displayCurrentUser,
  user,
  ...rest
}) => {
  // Hooks
  const { t } = useTranslation();
  const trackContent = useContentTracking();
  // State
  const [value, setValue] = useDebounceValue('', 300);
  const [selectedUser, setSelectedUser] = useState<FindUserData | null>(
    user || null
  );
  // Event handlers
  const handleConfirm = useCallback(() => {
    if (!selectedUser) return;
    onConfirm(selectedUser);
    handleDismiss();
  }, [selectedUser, onConfirm, handleDismiss]);

  useEffect(() => {
    trackContent('find-user', { type: 'sider' });
  }, [trackContent]);

  return (
    <Sheets
      {...rest}
      data-testid="findUserSider"
      aria-label={t('page.findUser.title')}
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={t('page.findUser.title')}
          backBtnClick={onBack}
        />
      }
      footer={
        <ModalFooter>
          <Button
            data-testid="confirmUserBtn"
            type="primary"
            onClick={handleConfirm}
            disabled={!selectedUser}
          >
            {t('label.confirm')}
          </Button>
        </ModalFooter>
      }
    >
      <ModalBody>
        <p className="mb-4">{descriptionText}</p>

        <div className="pb-5">
          <Search
            ariaLabel={t('aria.search')}
            clearBtnText={t('aria.clearSearch')}
            id="search"
            onSearch={(_, context) => {
              setValue(context.value || '');
            }}
            placeholder={t('label.search')}
            onChange={(e) => {
              setValue(e.target.value || '');
            }}
            submitBtnText={t('aria.submit')}
          />
        </div>

        <UserPicker
          search={value}
          selectedUser={selectedUser || undefined}
          onUserSelect={setSelectedUser}
          filterByLocation={filterByLocation}
          displayCurrentUser={displayCurrentUser}
        />
      </ModalBody>
    </Sheets>
  );
};

export default FindUserSider;
